import billingPolicy from '../../config/billingPolicy.json';
/**
 * UserMenu — shared user-menu helpers + mobile header avatar menu.
 *
 * Helpers (getUserInitials, getUserMenuOptions, handleUserMenuAction,
 * performLogout) are the single source of truth for the user menu; AppSidebar
 * consumes them for the desktop/tablet sidebar menu.
 *
 * <HeaderUserMenu> is the mobile-only avatar in the AppHeader (hidden ≥768px
 * via CSS). On mobile the sidebar is dropped entirely, so this menu also
 * carries Today/Patients navigation.
 *
 * See: docs/features/infrastructure/app-shell.md
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CalendarCheck,
    ContactRound,
    LogOut,
    CreditCard,
    Menu,
    UserRoundCog,
    UserPlus,
    Building2,
    CircleUser,
} from "lucide-react";
import { useAuth } from "../../utils/authContext";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import { logAuditEventAsync, AuditActions, clearAuditLoggerCache } from "../../utils/auditLogger";
import { meetsMinRole, getEffectiveInviteCap } from "../../utils/roleUtils";
import InviteModal from "../../utils/InviteModal";
export { getUserInitials } from "../../utils/userInitials";

/**
 * Build user-menu options based on role.
 * Keep the menu aligned with current role and account capabilities.
 */
export function getUserMenuOptions(userRole, userData, flags) {
    const options = [
        { label: "Sign Out", action: "logout", icon: <LogOut size={14} /> },
    ];
    if (billingPolicy.enabled && userData?.stripeCustomerId) {
        options.unshift({ label: "Manage Billing", action: "billing", icon: <CreditCard size={14} /> });
    }
    if (meetsMinRole(userRole, "standard") && getEffectiveInviteCap(userData) > 0) {
        options.unshift({ label: "Invite User", action: "invite", icon: <UserPlus size={14} /> });
    }
    // My Account is the home for profile + settings in v3. Settings (role-gated
    // to manager+) is reachable as a modal from the Account page, so we no
    // longer render a separate Settings menu entry here. The `userSettingsMenu`
    // feature flag now only gates whether the Settings modal link is rendered
    // inside Account.
    options.unshift({ label: "My Account", action: "account", icon: <CircleUser size={14} /> });
    if (meetsMinRole(userRole, "companyAdmin") && userData?.companyId) {
        options.unshift({ label: "My Team", action: "company-admin", icon: <Building2 size={14} /> });
    }
    if (meetsMinRole(userRole, "superAdmin")) {
        options.unshift({ label: "Admin", action: "admin", icon: <UserRoundCog size={14} /> });
    }
    return options;
}

/**
 * Sign out with audit logging, then route to login.
 */
export async function performLogout(signOut, navigate) {
    try {
        logAuditEventAsync(AuditActions.AUTH_LOGOUT, {});
        clearAuditLoggerCache();
        await signOut();
        navigate("/");
        return true;
    } catch (error) {
        console.error("Logout error:", error);
        return false;
    }
}

/**
 * Handle user-menu action (navigate, logout, billing).
 * Handle the selected menu action in AppShell.
 */
export async function handleUserMenuAction(action, setMenuOpen, handleLogout, navigate, userData, setIsInviteModalOpen) {
    setMenuOpen(false);
    if (action === "logout") handleLogout();
    if (action === "account") navigate("/account");
    if (action === "admin") navigate("/admin");
    if (action === "company-admin") navigate("/company-admin");
    if (action === "today") navigate("/dashboard");
    if (action === "patients") navigate("/patients");
    if (action === "invite" && setIsInviteModalOpen) setIsInviteModalOpen(true);
    if (billingPolicy.enabled && action === "billing" && userData?.stripeCustomerId) {
        try {
            const response = await fetch("/api/stripe/create-portal-session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    customerId: userData.stripeCustomerId,
                    returnUrl: window.location.href,
                }),
            });
            const data = await response.json();
            if (data.url) window.location.href = data.url;
        } catch (error) {
            console.error("Error opening billing portal:", error);
        }
    }
}

// Mobile-only nav entries — the sidebar (and its Today/Patients links) is
// hidden below 768px, so the header menu is the route to these pages.
const NAV_OPTIONS = [
    { label: "Today", action: "today", icon: <CalendarCheck size={14} /> },
    { label: "Patients", action: "patients", icon: <ContactRound size={14} /> },
];

/**
 * HeaderUserMenu — hamburger + dropdown in the AppHeader, mobile only.
 * Local open state (not the shared AppShellContext userMenuOpen), so it has
 * no auto-close timer and no coupling to the sidebar menu.
 */
const HeaderUserMenu = ({ userData }) => {
    const { signOut } = useAuth();
    const { flags } = useFeatureFlags();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    const handleLogout = () => performLogout(signOut, navigate);
    const handleMenuAction = (action) => {
        handleUserMenuAction(action, setMenuOpen, handleLogout, navigate, userData, setIsInviteModalOpen);
    };

    const accountOptions = getUserMenuOptions(userData?.userRole || "standard", userData, flags);

    return (
        <div className="app-shell-header-user">
            <span
                className="app-shell-header-action"
                onClick={() => setMenuOpen((o) => !o)}
                role="button"
                aria-label="Menu"
                aria-haspopup="true"
                aria-expanded={menuOpen}
            >
                <Menu className="app-shell-header-icon" size={25} />
            </span>
            {menuOpen && (
                <>
                    <div
                        className="app-shell-header-user-backdrop"
                        onClick={() => setMenuOpen(false)}
                    />
                    <div className="app-shell-user-menu">
                        {NAV_OPTIONS.map((option) => (
                            <div
                                key={option.action}
                                className="app-shell-user-menu-item"
                                onClick={() => handleMenuAction(option.action)}
                            >
                                {option.icon}
                                {option.label}
                            </div>
                        ))}
                        <hr className="app-shell-user-menu-divider" />
                        {accountOptions.map((option) => (
                            <div
                                key={option.action}
                                className="app-shell-user-menu-item"
                                onClick={() => handleMenuAction(option.action)}
                            >
                                {option.icon}
                                {option.label}
                            </div>
                        ))}
                    </div>
                </>
            )}
            <InviteModal
                isOpen={isInviteModalOpen}
                setIsOpen={setIsInviteModalOpen}
                userData={userData}
            />
        </div>
    );
};

export default HeaderUserMenu;
